"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const ClearanceContext = createContext({
  clearances: [],
});

export const ClearanceProvider = ({ children }) => {
  const [clearances, setClearances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ==========================
  // Fetch Clearances
  // ==========================
  const fetchClearances = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/clearance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch clearance records");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setClearances(data.data || []);
      } else {
        setError(data.message || "Failed to fetch clearance records");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch clearance records");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // Get Single Clearance
  // ==========================
  const getClearance = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/clearance/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to fetch clearance",
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to fetch clearance",
      };
    }
  };

  // ==========================
  // Create Clearance
  // payload: { employee_id, department, remarks, create_audit_id }
  // ==========================
  const createClearance = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/clearance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to create clearance request",
        };
      }

      if (data.status === 201 || data.status === 200) {
        await fetchClearances();

        toast.success(data.message || "Clearance request submitted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create clearance request",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to create clearance request",
      };
    }
  };

  // ==========================
  // Update Clearance
  // ==========================
  const updateClearance = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/clearance/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update clearance",
        };
      }

      if (data.status === 200) {
        await fetchClearances();

        toast.success(data.message || "Clearance updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update clearance",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update clearance",
      };
    }
  };

  // ==========================
  // Delete Clearance
  // ==========================
  const deleteClearance = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/clearance/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to delete clearance",
        };
      }

      if (data.status === 200) {
        await fetchClearances();

        toast.success(data.message || "Clearance deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete clearance",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete clearance",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchClearances();
    }
  }, []);

  return (
    <ClearanceContext.Provider
      value={{
        clearances,
        loading,
        error,
        fetchClearances,
        getClearance,
        createClearance,
        updateClearance,
        deleteClearance,
        setClearances,
      }}
    >
      {children}
    </ClearanceContext.Provider>
  );
};

export const useClearances = () => useContext(ClearanceContext);